import { AnalyzerConfig } from '../types/contractAnalyzer';

export interface ExtractionResult {
  text: string;
  meta: {
    filename: string;
    mimeType: string;
    size: number;
    pages?: number; // placeholder for future PDF extraction
  };
}

// Client-side extraction. TXT is fully supported. PDF/DOCX via lazy-loaded libs.
export async function extractContractText(file: File, _config?: AnalyzerConfig): Promise<ExtractionResult> {
  if (!file) throw new Error('A file is required for extraction');
  const mime = file.type || '';
  const name = file.name.toLowerCase();

  // TXT and RTF via File.text()
  const isPlainText = mime === 'text/plain' || name.endsWith('.txt') || name.endsWith('.rtf');
  if (isPlainText) {
    const text = await file.text();
    if (!text || !text.trim()) {
      throw new Error('The uploaded text file appears to be empty');
    }
    return {
      text,
      meta: { filename: file.name, mimeType: mime || 'text/plain', size: file.size }
    };
  }

  // PDF via pdfjs-dist (lazy)
  if (name.endsWith('.pdf')) {
    try {
      // @ts-ignore dynamic import at runtime
      const pdfjs = await import('pdfjs-dist/build/pdf');
      // Some builds require worker setup; pdfjs-dist auto worker if bundler supports
      const buf = await file.arrayBuffer();
      const loadingTask = pdfjs.getDocument({ data: buf });
      const pdf = await loadingTask.promise;
      let text = '';
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        const strings = (content.items || []).map((it: any) => it.str || '').join(' ');
        text += `\n\n[Page ${i}]\n${strings}`;
      }
      let trimmed = text.trim();
      // OCR fallback if no extractable text
      if (!trimmed || trimmed.replace(/[\s\n]+/g, '').length < 20) {
        try {
          // @ts-ignore lazy import tesseract.js
          const Tesseract = await import('tesseract.js');
          const ocrPages = Math.min(3, pdf.numPages); // limit for performance
          let ocrText = '';
          for (let i = 1; i <= ocrPages; i++) {
            const page = await pdf.getPage(i);
            const viewport = page.getViewport({ scale: 1.5 });
            // Create an offscreen canvas
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) continue;
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            await page.render({ canvasContext: ctx as any, viewport }).promise;
            const dataUrl = canvas.toDataURL('image/png');
            const { data: ocr } = await Tesseract.recognize(dataUrl, 'eng');
            if (ocr && ocr.text) {
              ocrText += `\n\n[OCR Page ${i}]\n${ocr.text}`;
            }
          }
          if (ocrText.trim()) {
            trimmed = ocrText.trim();
          }
        } catch (ocrErr) {
          console.warn('OCR fallback failed or tesseract not installed:', ocrErr);
        }
      }
      return {
        text: trimmed,
        meta: { filename: file.name, mimeType: mime || 'application/pdf', size: file.size, pages: pdf.numPages }
      };
    } catch (e) {
      console.warn('PDF extraction failed or pdfjs not installed:', e);
      throw new Error('PDF extraction requires additional components. Please upload a TXT version of the contract for now.');
    }
  }
  // DOCX via mammoth (lazy)
  if (name.endsWith('.doc') || name.endsWith('.docx')) {
    try {
      // @ts-ignore
      const mammoth = await import('mammoth');
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      const text = (result?.value || '').trim();
      if (!text) throw new Error('No text content found in DOCX file');
      return {
        text,
        meta: { filename: file.name, mimeType: mime || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', size: file.size }
      };
    } catch (e) {
      console.warn('DOCX extraction failed or mammoth not installed:', e);
      throw new Error('DOC/DOCX extraction requires additional components. Please upload a TXT version of the contract for now.');
    }
  }

  // Fallback
  throw new Error('Unsupported file format. Please upload a TXT file while the Beta is in development.');
}
